// utils/emailTemplateUtils.js

// Email template validation functions
export const validateEmailTemplate = (template) => {
    const errors = {};

    if (!template.name || template.name.trim().length === 0) {
        errors.name = 'Template name is required';
    }

    if (!template.type) {
        errors.type = 'Template type is required';
    }

    if (!template.subject || template.subject.trim().length === 0) {
        errors.subject = 'Email subject is required';
    }

    if (!template.htmlContent || template.htmlContent.trim().length === 0) {
        errors.htmlContent = 'HTML content is required';
    }

    if (!template.fromEmail || template.fromEmail.trim().length === 0) {
        errors.fromEmail = 'From email is required';
    } else if (!isValidEmail(template.fromEmail)) {
        errors.fromEmail = 'Please enter a valid email address';
    }

    if (template.replyTo && !isValidEmail(template.replyTo)) {
        errors.replyTo = 'Please enter a valid email address';
    }

    return {
        isValid: Object.keys(errors).length === 0,
        errors,
    };
};

const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};

// Default template content generators
export const getDefaultTemplateContent = (type) => {
    const templates = {
        'order_confirmation': {
            subject: 'Order Confirmation - {{order_id}}',
            htmlContent: `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <h1>Order Confirmation</h1>
  <p>Dear {{user_name}},</p>
  <p>Thank you for your order! We have received your order and it is being processed.</p>
  
  <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
    <h3>Order Details</h3>
    <p><strong>Order ID:</strong> {{order_id}}</p>
    <p><strong>Total Amount:</strong> {{total_price}}</p>
    <p><strong>Order Type:</strong> {{order_type}}</p>
    <p><strong>Restaurant:</strong> {{merchant_name}}</p>
    <p><strong>Items:</strong> {{order_items}}</p>
  </div>
  
  <p>We'll notify you when your order is ready!</p>
  <p>Thank you for choosing Elio & Papas!</p>
</div>`,
            textContent: `Order Confirmation

Dear {{user_name}},

Thank you for your order! We have received your order and it is being processed.

Order Details:
Order ID: {{order_id}}
Total Amount: {{total_price}}
Order Type: {{order_type}}
Restaurant: {{merchant_name}}
Items: {{order_items}}

We'll notify you when your order is ready!

Thank you for choosing Elio & Papas!`
        },

        'user_registration': {
            subject: 'Welcome to {{app_name}} - Please verify your email',
            htmlContent: `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <h1>Welcome to {{app_name}}!</h1>
  <p>Dear {{user_name}},</p>
  <p>Thank you for registering with {{app_name}}. To complete your registration, please verify your email address.</p>
  
  <div style="text-align: center; margin: 30px 0;">
    <a href="{{verification_url}}" style="background-color: #007bff; color: white; padding: 15px 30px; text-decoration: none; border-radius: 5px;">
      Verify Email Address
    </a>
  </div>
  
  <p>If the button doesn't work, you can also verify using this code: <strong>{{verification_code}}</strong></p>
  
  <p>If you didn't create this account, please ignore this email.</p>
  
  <p>Best regards,<br>The {{app_name}} Team</p>
</div>`,
            textContent: `Welcome to {{app_name}}!

Dear {{user_name}},

Thank you for registering with {{app_name}}. To complete your registration, please verify your email address.

Please visit: {{verification_url}}

Or use this verification code: {{verification_code}}

If you didn't create this account, please ignore this email.

Best regards,
The {{app_name}} Team`
        },

        'password_reset': {
            subject: 'Reset Your Password - {{app_name}}',
            htmlContent: `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <h1>Password Reset Request</h1>
  <p>Dear {{user_name}},</p>
  <p>We received a request to reset your password. Click the button below to reset it:</p>
  
  <div style="text-align: center; margin: 30px 0;">
    <a href="{{reset_url}}" style="background-color: #dc3545; color: white; padding: 15px 30px; text-decoration: none; border-radius: 5px;">
      Reset Password
    </a>
  </div>
  
  <p>This link will expire in {{expiry_time}}.</p>
  
  <p>If you didn't request this password reset, please ignore this email.</p>
  
  <p>Best regards,<br>The {{app_name}} Team</p>
</div>`,
            textContent: `Password Reset Request

Dear {{user_name}},

We received a request to reset your password. Please visit: {{reset_url}}

This link will expire in {{expiry_time}}.

If you didn't request this password reset, please ignore this email.

Best regards,
The {{app_name}} Team`
        },

        'order_ready': {
            subject: 'Your Order is Ready! - {{order_id}}',
            htmlContent: `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <h1>🍽️ Your Order is Ready!</h1>
  <p>Hi {{user_name}},</p>
  <p>Great news! Your order from {{merchant_name}} is ready for pickup.</p>
  
  <div style="background-color: #d4edda; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #28a745;">
    <h3>Pickup Information</h3>
    <p><strong>Order ID:</strong> {{order_id}}</p>
    <p><strong>Restaurant:</strong> {{merchant_name}}</p>
    <p><strong>Location:</strong> {{store_name}}</p>
    <p><strong>Ready Time:</strong> {{ready_time}}</p>
  </div>
  
  <p><strong>Pickup Instructions:</strong> {{pickup_instructions}}</p>
  
  <p>Thank you for your order!</p>
</div>`,
            textContent: `Your Order is Ready!

Hi {{user_name}},

Great news! Your order from {{merchant_name}} is ready for pickup.

Pickup Information:
Order ID: {{order_id}}
Restaurant: {{merchant_name}}
Location: {{store_name}}
Ready Time: {{ready_time}}

Pickup Instructions: {{pickup_instructions}}

Thank you for your order!`
        },

        'order_cancelled': {
            subject: 'Order Cancelled - {{order_id}}',
            htmlContent: `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <h1>Order Cancelled</h1>
  <p>Dear {{user_name}},</p>
  <p>We're sorry to inform you that your order has been cancelled.</p>
  
  <div style="background-color: #f8d7da; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #dc3545;">
    <h3>Order Information</h3>
    <p><strong>Order ID:</strong> {{order_id}}</p>
    <p><strong>Total Amount:</strong> {{total_price}}</p>
    <p><strong>Restaurant:</strong> {{merchant_name}}</p>
    <p><strong>Reason:</strong> {{cancellation_reason}}</p>
  </div>
  
  <p>Your refund of {{refund_amount}} will be processed within 3-5 business days.</p>
  
  <p>We apologize for any inconvenience caused.</p>
</div>`,
            textContent: `Order Cancelled

Dear {{user_name}},

We're sorry to inform you that your order has been cancelled.

Order Information:
Order ID: {{order_id}}
Total Amount: {{total_price}}
Restaurant: {{merchant_name}}
Reason: {{cancellation_reason}}

Your refund of {{refund_amount}} will be processed within 3-5 business days.

We apologize for any inconvenience caused.`
        },

        'order_delivered': {
            subject: 'Order Delivered - {{order_id}}',
            htmlContent: `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <h1>✅ Order Delivered!</h1>
  <p>Hi {{user_name}},</p>
  <p>Your order from {{merchant_name}} has been successfully delivered!</p>
  
  <div style="background-color: #d4edda; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #28a745;">
    <h3>Delivery Information</h3>
    <p><strong>Order ID:</strong> {{order_id}}</p>
    <p><strong>Restaurant:</strong> {{merchant_name}}</p>
    <p><strong>Delivered at:</strong> {{delivery_time}}</p>
  </div>
  
  <div style="text-align: center; margin: 30px 0;">
    <a href="{{feedback_url}}" style="background-color: #007bff; color: white; padding: 15px 30px; text-decoration: none; border-radius: 5px;">
      Rate Your Experience
    </a>
  </div>
  
  <p>We hope you enjoyed your meal!</p>
</div>`,
            textContent: `Order Delivered!

Hi {{user_name}},

Your order from {{merchant_name}} has been successfully delivered!

Delivery Information:
Order ID: {{order_id}}
Restaurant: {{merchant_name}}
Delivered at: {{delivery_time}}

Rate your experience: {{feedback_url}}

We hope you enjoyed your meal!`
        },

        'user_welcome': {
            subject: 'Welcome to Elio & Papas! 🎉',
            htmlContent: `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <h1>🎉 Welcome to Elio & Papas!</h1>
  <p>Hi {{user_name}},</p>
  <p>Welcome to our community! We're excited to have you on board.</p>
  
  <div style="background-color: #e7f3ff; padding: 20px; border-radius: 8px; margin: 20px 0;">
    <h3>Get Started</h3>
    <p>Here's what you can do next:</p>
    <ul>
      <li>Browse restaurants in your area</li>
      <li>Place your first order</li>
      <li>Earn loyalty points with every purchase</li>
      <li>Track your orders in real-time</li>
    </ul>
  </div>
  
  <div style="text-align: center; margin: 30px 0;">
    <a href="{{app_url}}" style="background-color: #28a745; color: white; padding: 15px 30px; text-decoration: none; border-radius: 5px;">
      Start Ordering
    </a>
  </div>
  
  <p>If you have any questions, feel free to contact us at {{support_email}}</p>
  
  <p>Happy ordering! 🍽️</p>
</div>`,
            textContent: `Welcome to Elio & Papas!

Hi {{user_name}},

Welcome to our community! We're excited to have you on board.

Get Started:
- Browse restaurants in your area
- Place your first order
- Earn loyalty points with every purchase
- Track your orders in real-time

Start ordering: {{app_url}}

If you have any questions, feel free to contact us at {{support_email}}

Happy ordering!`
        },

        'email_verification': {
            subject: 'Please verify your email address',
            htmlContent: `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <h1>Email Verification Required</h1>
  <p>Hi {{user_name}},</p>
  <p>Please verify your email address to complete your account setup.</p>
  
  <div style="text-align: center; margin: 30px 0;">
    <a href="{{verification_url}}" style="background-color: #007bff; color: white; padding: 15px 30px; text-decoration: none; border-radius: 5px;">
      Verify Email
    </a>
  </div>
  
  <p>Or use this verification code: <strong>{{verification_code}}</strong></p>
  
  <p>If you didn't request this verification, please ignore this email.</p>
</div>`,
            textContent: `Email Verification Required

Hi {{user_name}},

Please verify your email address to complete your account setup.

Verification URL: {{verification_url}}
Verification Code: {{verification_code}}

If you didn't request this verification, please ignore this email.`
        },

        'loyalty_points_earned': {
            subject: 'You Earned {{points_earned}} Points! 🎉',
            htmlContent: `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <h1>🎉 Points Earned!</h1>
  <p>Hi {{user_name}},</p>
  <p>Congratulations! You've earned loyalty points with your recent order.</p>
  
  <div style="background-color: #fff3cd; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #ffc107;">
    <h3>Points Summary</h3>
    <p><strong>Points Earned:</strong> {{points_earned}}</p>
    <p><strong>Total Points:</strong> {{total_points}}</p>
    <p><strong>Restaurant:</strong> {{merchant_name}}</p>
    <p><strong>Order ID:</strong> {{order_id}}</p>
  </div>
  
  <p>Keep collecting points to unlock amazing rewards!</p>
</div>`,
            textContent: `Points Earned!

Hi {{user_name}},

Congratulations! You've earned loyalty points with your recent order.

Points Summary:
Points Earned: {{points_earned}}
Total Points: {{total_points}}
Restaurant: {{merchant_name}}
Order ID: {{order_id}}

Keep collecting points to unlock amazing rewards!`
        },

        'loyalty_points_redeemed': {
            subject: 'Points Redeemed Successfully! 💰',
            htmlContent: `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <h1>💰 Points Redeemed!</h1>
  <p>Hi {{user_name}},</p>
  <p>You've successfully redeemed your loyalty points!</p>
  
  <div style="background-color: #d1ecf1; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #bee5eb;">
    <h3>Redemption Details</h3>
    <p><strong>Points Redeemed:</strong> {{points_redeemed}}</p>
    <p><strong>Discount Amount:</strong> {{discount_amount}}</p>
    <p><strong>Remaining Points:</strong> {{remaining_points}}</p>
    <p><strong>Restaurant:</strong> {{merchant_name}}</p>
    <p><strong>Order ID:</strong> {{order_id}}</p>
  </div>
  
  <p>Thanks for being a loyal customer!</p>
</div>`,
            textContent: `Points Redeemed!

Hi {{user_name}},

You've successfully redeemed your loyalty points!

Redemption Details:
Points Redeemed: {{points_redeemed}}
Discount Amount: {{discount_amount}}
Remaining Points: {{remaining_points}}
Restaurant: {{merchant_name}}
Order ID: {{order_id}}

Thanks for being a loyal customer!`
        },

        'merchant_registration': {
            subject: 'Merchant Registration Received',
            htmlContent: `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <h1>Registration Received</h1>
  <p>Dear {{merchant_name}},</p>
  <p>Thank you for applying to become a merchant partner with {{app_name}}.</p>
  
  <div style="background-color: #e7f3ff; padding: 20px; border-radius: 8px; margin: 20px 0;">
    <h3>What's Next?</h3>
    <p>Our team will review your application within 2-3 business days.</p>
    <p>We'll notify you once your account has been approved.</p>
  </div>
  
  <p>Registration Date: {{registration_date}}</p>
  <p>Status: {{approval_status}}</p>
  
  <p>If you have any questions, please contact our support team.</p>
</div>`,
            textContent: `Registration Received

Dear {{merchant_name}},

Thank you for applying to become a merchant partner with {{app_name}}.

What's Next?
Our team will review your application within 2-3 business days.
We'll notify you once your account has been approved.

Registration Date: {{registration_date}}
Status: {{approval_status}}

If you have any questions, please contact our support team.`
        },

        'merchant_approval': {
            subject: 'Your Merchant Account Has Been Approved! 🎉',
            htmlContent: `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <h1>🎉 Welcome to {{app_name}}!</h1>
  <p>Dear {{merchant_name}},</p>
  <p>Congratulations! Your merchant account has been approved and is now active.</p>
  
  <div style="background-color: #d4edda; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #28a745;">
    <h3>Next Steps</h3>
    <p>1. Log in to your merchant dashboard</p>
    <p>2. Set up your menu and store information</p>
    <p>3. Start receiving orders!</p>
  </div>
  
  <div style="text-align: center; margin: 30px 0;">
    <a href="{{login_url}}" style="background-color: #28a745; color: white; padding: 15px 30px; text-decoration: none; border-radius: 5px;">
      Login to Dashboard
    </a>
  </div>
  
  <p>If you need any assistance, please contact us at {{support_email}}</p>
  
  <p>Welcome aboard!</p>
</div>`,
            textContent: `Welcome to {{app_name}}!

Dear {{merchant_name}},

Congratulations! Your merchant account has been approved and is now active.

Next Steps:
1. Log in to your merchant dashboard
2. Set up your menu and store information
3. Start receiving orders!

Login URL: {{login_url}}

If you need any assistance, please contact us at {{support_email}}

Welcome aboard!`
        },

        'merchant_rejection': {
            subject: 'Update on Your Merchant Application',
            htmlContent: `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <h1>Application Update</h1>
  <p>Dear {{merchant_name}},</p>
  <p>Thank you for your interest in becoming a merchant partner with {{app_name}}.</p>
  
  <div style="background-color: #f8d7da; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #dc3545;">
    <h3>Application Status</h3>
    <p>Unfortunately, we're unable to approve your application at this time.</p>
    <p><strong>Reason:</strong> {{rejection_reason}}</p>
  </div>
  
  <p>You're welcome to reapply once you've addressed the concerns mentioned above.</p>
  
  <div style="text-align: center; margin: 30px 0;">
    <a href="{{reapply_url}}" style="background-color: #007bff; color: white; padding: 15px 30px; text-decoration: none; border-radius: 5px;">
      Reapply Now
    </a>
  </div>
  
  <p>If you have any questions, please contact our support team at {{support_email}}</p>
</div>`,
            textContent: `Application Update

Dear {{merchant_name}},

Thank you for your interest in becoming a merchant partner with {{app_name}}.

Application Status:
Unfortunately, we're unable to approve your application at this time.
Reason: {{rejection_reason}}

You're welcome to reapply once you've addressed the concerns mentioned above.

Reapply: {{reapply_url}}

If you have any questions, please contact our support team at {{support_email}}`
        },

        'promotional': {
            subject: '{{promotion_title}} - Limited Time Offer!',
            htmlContent: `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <h1>🎁 {{promotion_title}}</h1>
  <p>Hi {{user_name}},</p>
  <p>We have an exciting offer just for you!</p>
  
  <div style="background-color: #e7f3ff; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #007bff;">
    <h3>Special Offer</h3>
    <p>{{promotion_description}}</p>
    
    <div style="text-align: center; margin: 20px 0;">
      <div style="background-color: #007bff; color: white; padding: 10px 20px; border-radius: 5px; display: inline-block; font-size: 18px; font-weight: bold;">
        Code: {{discount_code}}
      </div>
    </div>
    
    <p><strong>Valid until:</strong> {{expiry_date}}</p>
  </div>
  
  <p><small><a href="{{terms_url}}">Terms and conditions apply</a></small></p>
  
  <p>Happy ordering! 🍽️</p>
</div>`,
            textContent: `{{promotion_title}}

Hi {{user_name}},

We have an exciting offer just for you!

Special Offer:
{{promotion_description}}

Discount Code: {{discount_code}}
Valid until: {{expiry_date}}

Terms and conditions: {{terms_url}}

Happy ordering!`
        },

        'newsletter': {
            subject: '{{newsletter_title}} - {{app_name}} Newsletter',
            htmlContent: `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <h1>{{newsletter_title}}</h1>
  <p>Hi {{user_name}},</p>
  
  <div style="background-color: #f8f9fa; padding: 20px; border-radius: 8px; margin: 20px 0;">
    {{newsletter_content}}
  </div>
  
  <hr style="margin: 30px 0;">
  
  <p style="font-size: 12px; color: #666;">
    You're receiving this newsletter because you subscribed to {{app_name}} updates.
    <br>
    <a href="{{unsubscribe_url}}">Unsubscribe</a> from future newsletters.
  </p>
</div>`,
            textContent: `{{newsletter_title}}

Hi {{user_name}},

{{newsletter_content}}

---

You're receiving this newsletter because you subscribed to {{app_name}} updates.
Unsubscribe: {{unsubscribe_url}}`
        },

        'account_suspension': {
            subject: 'Important: Account Suspended',
            htmlContent: `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <h1>Account Suspended</h1>
  <p>Dear {{user_name}},</p>
  <p>We're writing to inform you that your account has been temporarily suspended.</p>
  
  <div style="background-color: #f8d7da; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #dc3545;">
    <h3>Suspension Details</h3>
    <p><strong>Reason:</strong> {{suspension_reason}}</p>
    <p><strong>Date:</strong> {{suspension_date}}</p>
  </div>
  
  <p>If you believe this is an error or would like to appeal this decision, please contact us.</p>
  
  <div style="text-align: center; margin: 30px 0;">
    <a href="{{appeal_url}}" style="background-color: #007bff; color: white; padding: 15px 30px; text-decoration: none; border-radius: 5px;">
      Submit Appeal
    </a>
  </div>
</div>`,
            textContent: `Account Suspended

Dear {{user_name}},

We're writing to inform you that your account has been temporarily suspended.

Suspension Details:
Reason: {{suspension_reason}}
Date: {{suspension_date}}

If you believe this is an error or would like to appeal this decision, please contact us.

Submit Appeal: {{appeal_url}}`
        },

        'account_reactivation': {
            subject: 'Your Account Has Been Reactivated! 🎉',
            htmlContent: `
<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto;">
  <h1>🎉 Account Reactivated!</h1>
  <p>Dear {{user_name}},</p>
  <p>Good news! Your account has been reactivated and you can now access all features.</p>
  
  <div style="background-color: #d4edda; padding: 20px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #28a745;">
    <h3>Account Status</h3>
    <p><strong>Status:</strong> Active</p>
    <p><strong>Reactivated on:</strong> {{reactivation_date}}</p>
  </div>
  
  <div style="text-align: center; margin: 30px 0;">
    <a href="{{login_url}}" style="background-color: #28a745; color: white; padding: 15px 30px; text-decoration: none; border-radius: 5px;">
      Login to Your Account
    </a>
  </div>
  
  <p>Welcome back!</p>
</div>`,
            textContent: `Account Reactivated!

Dear {{user_name}},

Good news! Your account has been reactivated and you can now access all features.

Account Status: Active
Reactivated on: {{reactivation_date}}

Login: {{login_url}}

Welcome back!`
        }
    };

    return templates[type] || {
        subject: 'New Email Template',
        htmlContent: '<p>Enter your email content here...</p>',
        textContent: 'Enter your email content here...'
    };
};

// Error handling helper
export const handleEmailError = (error, toast, operation = 'email operation') => {
    console.error(`Email ${operation} failed:`, error);

    let errorMessage = `Failed to complete ${operation}`;

    if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
    } else if (error.message) {
        errorMessage = error.message;
    }

    toast({
        title: 'Email Error',
        description: errorMessage,
        status: 'error',
        duration: 5000,
        isClosable: true,
    });
};

// Permission checking utility
export const hasEmailTemplatePermission = (user) => {
    return user && (user.role === 'admin' || user.permissions?.includes('manage_email_templates'));
};

// Template variable extraction utility
export const extractVariablesFromContent = (content) => {
    const variableRegex = /\{\{([^}]+)\}\}/g;
    const variables = new Set();
    let match;

    while ((match = variableRegex.exec(content)) !== null) {
        variables.add(`{{${match[1].trim()}}}`);
    }

    return Array.from(variables);
};

// Generate default test data for different template types
export const getDefaultTestData = (templateType) => {
    const baseData = {
        user_name: 'John Doe',
        user_phone: '+1234567890',
        user_email: 'john.doe@example.com',
        app_name: 'Elio & Papas',
        app_url: 'https://starntop.com',
        support_email: 'elio@starntop.com',
        company_name: 'Elio & Papas',
        company_email: 'elio@starntop.com',
    };

    const typeSpecificData = {
        'order_confirmation': {
            ...baseData,
            order_id: '12345',
            total_price: '$25.99',
            tip: '$3.00',
            redeem_discount: '$5.00',
            order_type: 'Dine In',
            order_status: 'confirmed',
            merchant_name: 'Test Restaurant',
            store_name: 'Downtown Location',
            order_date: new Date().toLocaleDateString(),
            order_items: 'Pizza Margherita x1, Coca Cola x2, Caesar Salad x1',
            tracking_url: 'https://yourapp.com/track/12345',
            tracking_id: 'TRK12345',
            provide_utensils: 'Yes',
            paid: 'Yes',
        },
        'order_ready': {
            ...baseData,
            order_id: '12345',
            merchant_name: 'Test Restaurant',
            store_name: 'Downtown Location',
            ready_time: new Date().toLocaleTimeString(),
            ready_at: new Date().toLocaleString(),
            pickup_instructions: 'Please come to the front counter and show your order ID',
        },
        'order_cancelled': {
            ...baseData,
            order_id: '12345',
            total_price: '$25.99',
            merchant: ''
        }
    }
    return typeSpecificData
}

export const isValidJSON = (str) => {
    try {
        JSON.parse(str);
        return true;
    } catch (e) {
        return false;
    }
};
